import { Box, Button, Flex, Icon, IconButton, Input, Popover, PopoverArrow, PopoverBody, PopoverContent, PopoverTrigger, Spinner, Text, Tooltip, useDisclosure } from '@chakra-ui/react'
import { Save } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  useReactFlow,
  type Node,
  type Edge,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import type { KrakenGraphResponse, KrakenNode, KrakenEdge } from '../../../../types/kraken.types'
import {
  nodeTypes,
  edgeTypes,
  transformKrakenResponse,
  collapseRelations,
  radialTreeLayout,
  CREATE_ENTITY_KINDS,
  type CircleNodeData,
} from '../KrakenNetwork'
import EdgeFilterPanel from '../EdgeFilterPanel'
import { useGraphFiltersStore } from '../../../../stores/graphFiltersStore'
import { edgeFilterKey, dedupeEdges } from '../../../../utils/functions/krakenUtils'
import AddEntityModal from '../../CustomEntities/EntityList/AddEntityModal'
import type { EntityPreLink, CustomEntity } from '../../../../types/customEntities.types'

// Map a Kraken vertex (kind + id) to the Ocean detail-page route, if any.
export const targetRouteForNode = (data: Partial<CircleNodeData> | undefined): string | null => {
  if (!data) return null
  const original = data.original as { id?: string } | undefined
  const id = (original?.id as string | undefined) ?? ''
  if (!id) return null
  const stripPrefix = (prefix: string) => (id.startsWith(prefix) ? id.slice(prefix.length) : id)
  if (data.kind === 'SELLER') return `/react/sellers/${stripPrefix('seller_')}`
  if (data.kind === 'ITEM') return `/react/items/${stripPrefix('item_')}`
  if (data.kind === 'SAW') return `/react/saw/sites/${stripPrefix('saw_')}`
  // Custom entities/products open their list with the record focused (same
  // deep-link the chat chips use in KrakenChatMessage) — a bare list route
  // wouldn't land on the specific record.
  if (data.kind === 'ENTITY') return `/react/custom-entities/entities?focus=${stripPrefix('entity_')}`
  if (data.kind === 'PRODUCT') return `/react/custom-entities/products?focus=${stripPrefix('product_')}`
  if (data.kind === 'SEIZURE') return `/react/custom-entities/seizures/${stripPrefix('seizure_')}`
  return null
}

// Map a node kind to the M2M field name on the CustomEntity create payload.
const KIND_TO_PRELINK_FIELD: Record<string, EntityPreLink['field']> = {
  SELLER: 'sellers',
  ITEM: 'items',
  SAW: 'saws',
  ENTITY: 'related_entities',
  SEIZURE: 'seizures',
}

// Strip the Neptune vertex-id prefix (e.g. "seller_<uuid>" → "<uuid>").
const stripVertexPrefix = (id: string): string => {
  const idx = id.indexOf('_')
  return idx >= 0 ? id.slice(idx + 1) : id
}

/** Deduplicate and merge new nodes/edges into an existing KrakenGraphResponse. */
export const mergeGraphData = (
  prev: KrakenGraphResponse,
  newNodes: KrakenNode[],
  newEdges: KrakenEdge[],
  replaceNodeId?: string,
): KrakenGraphResponse => {
  // Optionally REPLACE a node: drop it but REWIRE its edges onto the new node,
  // so whatever hung off it (a site's items, a post's links) stays connected —
  // no orphans and no duplicate.
  const doReplace =
    !!replaceNodeId && replaceNodeId !== prev.root && !newNodes.some((n) => n.id === replaceNodeId)
  const newNodeId = doReplace
    ? newNodes.find((n) => n.id !== replaceNodeId)?.id ?? newNodes[0]?.id
    : undefined
  const baseNodes = doReplace ? prev.nodes.filter((n) => n.id !== replaceNodeId) : prev.nodes
  const baseEdges = !doReplace
    ? prev.edges
    : newNodeId
      ? prev.edges
          .map((e) => ({
            ...e,
            from_id: e.from_id === replaceNodeId ? newNodeId : e.from_id,
            to_id: e.to_id === replaceNodeId ? newNodeId : e.to_id,
          }))
          .filter((e) => e.from_id !== e.to_id)
      : prev.edges.filter((e) => e.from_id !== replaceNodeId && e.to_id !== replaceNodeId)
  const existingNodeIds = new Set(baseNodes.map((n) => n.id))
  const existingEdgeIds = new Set(baseEdges.map((e) => `${e.from_id}->${e.to_id}-${e.label}`))
  const dedupedNodes = newNodes.filter((n) => !existingNodeIds.has(n.id))
  const dedupedEdges = newEdges.filter((e) => !existingEdgeIds.has(`${e.from_id}->${e.to_id}-${e.label}`))
  return {
    ...prev,
    nodes: [...baseNodes, ...dedupedNodes],
    edges: [...baseEdges, ...dedupedEdges],
    node_count: baseNodes.length + dedupedNodes.length,
    edge_count: baseEdges.length + dedupedEdges.length,
  }
}

// Only genuine Ocean entities (rows in our own graph) can be expanded — the
// expand endpoint walks their neighbours in Neptune. Web-discovered / external
// nodes (Firecrawl matches, social profiles, bare links) have virtual ids and
// no neighbours to fetch, so they must not get an expand affordance.
const EXPANDABLE_KINDS = new Set<CircleNodeData['kind']>([
  'SELLER', 'ITEM', 'SAW', 'SOCIAL', 'PLATFORM', 'ENTITY', 'PRODUCT',
  'SEIZURE', 'REGISTRAR', 'REGISTRANT', 'ISP', 'EMAIL', 'PHONE',
  // Member-portal records pulled into the graph (case_/subcase_/contact_).
  'CASE', 'SUBCASE', 'CONTACT',
])
export const isOceanInternalNode = (data: Partial<CircleNodeData> | undefined): boolean => {
  if (!data) return false
  const original = data.original as { id?: string; source?: string } | undefined
  const source = String(original?.source ?? '').toLowerCase()
  if (source.startsWith('firecrawl') || source === 'apify') return false // web-discovered / external
  const id = String(original?.id ?? '')
  // Virtual/synthetic ids with no Neptune neighbours: web-discovery, reverse-image
  // matches, and apify social posts (`post_<platform>_<hash>`). These can be turned
  // into a manual entity/product but must not offer an (impossible) Expand.
  if (id.startsWith('discovery_') || id.startsWith('web_') || id.startsWith('post_')) return false
  return EXPANDABLE_KINDS.has(data.kind as CircleNodeData['kind'])
}

interface KrakenNetworkTreeProps {
  networkData: KrakenGraphResponse | null
  isLoading: boolean
  rootId?: string
  rootType?: string
  /**
   * Called when the user clicks a node. The host streams the response from
   * /kraken/expand/<vertex_id>/ and merges incoming nodes/edges back into
   * `networkData` so the panel grows in-place.
   */
  onExpandNode?: (vertexId: string) => void
  /** Set of vertex ids currently being expanded; used to show a loading cue. */
  expandingNodeIds?: Set<string>
  /**
   * Called when the user clicks the "Discover web" button on a node. The host
   * streams /kraken/discover-web/<vertex_id>/ and merges incoming graph deltas
   * + pushes the markdown summary into the chat thread.
   */
  onDiscoverWebNode?: (
    vertexId: string,
    opts: { layers: number; categories?: string[] },
  ) => void
  /** Set of vertex ids currently running web discovery; drives the spinner. */
  discoveringNodeIds?: Set<string>
  /** Called when the user clicks "Reverse image search" on an image-bearing
   *  node. Receives the image URL to search. */
  onReverseImageSearchNode?: (imageUrl: string) => void
  /** Called when the user clicks "Investigate email/phone OSINT" on an
   *  identifier node. Receives the email / phone value. */
  onInvestigateEmailOsintNode?: (email: string) => void
  onInvestigatePhoneOsintNode?: (phone: string) => void
  /**
   * Called when the user clicks the "+" on a discovered LINK node that is NOT
   * yet in Ocean. The host runs the direct create-entity flow (scrape → reason
   * type → create CustomEntity) headlessly and shows a spinner + toast.
   */
  onCreateEntityNode?: (vertexId: string) => void
  /** Set of vertex ids currently running the create-entity flow; drives the
   *  "+" button spinner. */
  creatingEntityNodeIds?: Set<string>
  /** Vertex ids for which an entity was created this session — hides the
   *  "Create entity with AI" button optimistically, before a graph reload. */
  createdEntityNodeIds?: Set<string>
  /** Show the edge weight + type filter overlay (default true). */
  showEdgeFilter?: boolean
  /** Called when the user saves the graph with a name. */
  onSaveGraph?: (name: string) => void
  /** Whether a graph save is in progress. */
  isSavingGraph?: boolean
  /** Called when edge filters change so the parent can track active filters. */
  onFilterChange?: (filters: { edgeTypes: string[]; minWeight: number }) => void
  /** Stable graph identity (saved-graph id / chat conversation id). When set,
   *  the Min-strength slider is persisted per-graph and restored on reload. */
  persistKey?: string
  /** Merge synthetic nodes/edges into the graph (e.g. after creating an entity). */
  onMergeGraphData?: (nodes: KrakenNode[], edges: KrakenEdge[]) => void
}

// Pick the cardinal direction (top/right/bottom/left) closest to an angle in
// radians. Used to align each edge with whichever side of the source and
// target nodes points toward the other — avoids the bezier "swing around"
// effect when handles are fixed to one side.
// A relation's "strength" for the Min-strength slider. Neptune sends `weight`
// (typically 1-7), the discovery pipeline sends `score` (0-100). Both are
// normalised to a 0-100 scale: weight is multiplied by 10, score is as-is.
// Returns NaN for edges with no numeric strength (weightless / structural) so
// callers can choose to always keep them.
const edgeStrengthOf = (e: { weight?: number; score?: number; confidence?: number; properties?: Record<string, unknown> }): number => {
  const p = e.properties || {}
  // Prefer weight (Neptune) — scale from ~1-10 → ~10-100.
  for (const v of [e.weight, p.weight]) {
    const n = typeof v === 'string' ? Number(v) : v
    if (typeof n === 'number' && !Number.isNaN(n)) return n * 10
  }
  // Fallback to score / confidence (web discovery) — already 0-100.
  for (const v of [e.score, p.score, e.confidence, p.confidence]) {
    const n = typeof v === 'string' ? Number(v) : v
    if (typeof n === 'number' && !Number.isNaN(n)) return n
  }
  return NaN
}

const cardinalForAngle = (angle: number): 'top' | 'right' | 'bottom' | 'left' => {
  // Normalise to [-π, π]
  const a = Math.atan2(Math.sin(angle), Math.cos(angle))
  if (a >= -Math.PI / 4 && a < Math.PI / 4) return 'right'
  if (a >= Math.PI / 4 && a < (3 * Math.PI) / 4) return 'bottom'
  if (a >= -(3 * Math.PI) / 4 && a < -Math.PI / 4) return 'top'
  return 'left'
}

const SaveGraphButton = ({ onSave, isSaving, left = '220px' }: { onSave: (name: string) => void; isSaving?: boolean; left?: string }) => {
  const [name, setName] = useState('')
  const [isOpen, setIsOpen] = useState(false)

  const handleSave = () => {
    onSave(name.trim())
    setName('')
    setIsOpen(false)
  }

  return (
    <Popover
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      placement="bottom-start"
      closeOnBlur
    >
      <PopoverTrigger>
        <IconButton
          aria-label="Save graph"
          icon={isSaving ? <Spinner size="xs" /> : <Icon as={Save} w="16px" h="16px" />}
          size="sm"
          position="absolute"
          top="10px"
          left={left}
          zIndex={10}
          bg="whiteAlpha.900"
          backdropFilter="blur(4px)"
          borderRadius="16px"
          boxShadow="sm"
          color="gray.500"
          _hover={{ bg: 'white', color: 'purple.500' }}
          isDisabled={isSaving}
          onClick={() => setIsOpen(true)}
        />
      </PopoverTrigger>
      <PopoverContent
        w="280px"
        borderRadius="14px"
        border="1px solid"
        borderColor="gray.100"
        boxShadow="0 12px 32px -8px rgba(0,0,0,0.18)"
        overflow="hidden"
        _focus={{ outline: 'none' }}
      >
        <PopoverArrow />
        <PopoverBody p={4}>
          <Text fontSize="13px" fontWeight="700" color="gray.800" letterSpacing="-0.01em" mb={1}>
            Save graph
          </Text>
          <Text fontSize="11px" color="gray.500" mb={3} lineHeight="1.4">
            Give this graph a name to find it later.
          </Text>
          <Input
            placeholder="e.g. VooFun investigation"
            size="sm"
            fontSize="13px"
            borderRadius="10px"
            bg="gray.50"
            borderColor="gray.200"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) handleSave() }}
            autoFocus
            mb={4}
            _hover={{ borderColor: 'gray.300' }}
            _focus={{ bg: 'white', borderColor: 'purple.400', boxShadow: '0 0 0 1px var(--chakra-colors-purple-400)' }}
          />
          <Flex justify="flex-end" align="center" gap={2}>
            <Button
              size="sm"
              variant="ghost"
              fontSize="13px"
              fontWeight="500"
              color="gray.600"
              onClick={() => setIsOpen(false)}
              _hover={{ bg: 'gray.100' }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              fontSize="13px"
              fontWeight="600"
              colorScheme="purple"
              borderRadius="10px"
              px={4}
              isDisabled={isSaving || !name.trim()}
              isLoading={isSaving}
              loadingText="Saving"
              onClick={handleSave}
            >
              Save
            </Button>
          </Flex>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}

const KrakenNetworkTreeInner = ({
  networkData,
  isLoading,
  rootId,
  onExpandNode,
  onReverseImageSearchNode,
  onInvestigateEmailOsintNode,
  onInvestigatePhoneOsintNode,
  expandingNodeIds,
  onDiscoverWebNode,
  discoveringNodeIds,
  onCreateEntityNode,
  creatingEntityNodeIds,
  createdEntityNodeIds,
  showEdgeFilter = true,
  onSaveGraph,
  isSavingGraph,
  onFilterChange,
  persistKey,
  onMergeGraphData,
}: KrakenNetworkTreeProps) => {
  // Which relation/domain bundles the user has expanded. Toggled by clicking a
  // GROUP node; drives collapseRelations below.
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())

  // ─── Create-entity modal (pre-linked to a graph node) ────────────────────
  const createEntityModal = useDisclosure()
  const [createEntityPreLink, setCreateEntityPreLink] = useState<EntityPreLink | null>(null)
  // Source node id (Neptune vertex) that triggered the create — used to build
  // the synthetic edge from this node to the newly created entity.
  const [createEntitySourceId, setCreateEntitySourceId] = useState<string | null>(null)

  const handleEntityCreated = useCallback((entity: CustomEntity, sourceNodeId: string | null) => {
    const entityVertexId = `entity_${entity.id}`
    const syntheticNode: KrakenNode = {
      id: entityVertexId,
      label: 'CustomEntity',
      name: entity.name,
      title: entity.name,
      url: entity.url ?? undefined,
      entity_type: entity.type,
    }
    const syntheticEdges: KrakenEdge[] = sourceNodeId
      ? [{
          id: `synth_${sourceNodeId}_${entityVertexId}`,
          label: 'linked_entity',
          from_id: sourceNodeId,
          to_id: entityVertexId,
          weight: 5,
        }]
      : []
    onMergeGraphData?.([syntheticNode], syntheticEdges)
  }, [onMergeGraphData])

  // ─── Edge filtering (weight slider + relation-type multi-select) ──────────
  // Lives here so every graph that renders through KrakenNetworkTree (the
  // Octopus AI page and the in-context seller/FAB graph) gets the same filter.
  const [activeEdgeFilters, setActiveEdgeFilters] = useState<Set<string>>(new Set())
  // Seed from the persisted per-graph value when this tree opens for a known
  // graph, so the slider starts where the user left it (0 otherwise).
  const getStoredMinStrength = useGraphFiltersStore((s) => s.getMinStrength)
  const setStoredMinStrength = useGraphFiltersStore((s) => s.setMinStrength)
  const [minEdgeWeight, setMinEdgeWeight] = useState(
    () => (persistKey ? getStoredMinStrength(persistKey) : undefined) ?? 0,
  )
  // The persistKey that `minEdgeWeight` currently reflects. On a graph switch,
  // `persistKey` changes a render before the restore effect resets the slider,
  // so during that window the on-screen value still belongs to the *previous*
  // graph. Persisting then would write the old value under the new key and
  // corrupt the new graph's saved threshold. Only persist once the on-screen
  // value has been settled for the current key (settledKeyRef === persistKey).
  const settledKeyRef = useRef(persistKey)

  useEffect(() => {
    onFilterChange?.({ edgeTypes: [...activeEdgeFilters], minWeight: minEdgeWeight })
  }, [activeEdgeFilters, minEdgeWeight])

  // Persist the slider value per-graph whenever a settled value changes, so it
  // survives a graph switch / page reload. Skipped while the value hasn't been
  // restored for the current key yet (see settledKeyRef). No-op without a key.
  useEffect(() => {
    if (persistKey && settledKeyRef.current === persistKey) {
      setStoredMinStrength(persistKey, minEdgeWeight)
    }
  }, [persistKey, minEdgeWeight, setStoredMinStrength])

  // Relation types present in the current graph (label → count), most common first.
  const availableEdgeTypes = useMemo(() => {
    const counts = dedupeEdges(networkData?.edges ?? []).reduce<Record<string, number>>((acc, e) => {
      const key = edgeFilterKey(e)
      acc[key] = (acc[key] || 0) + 1
      return acc
    }, {})
    return Object.entries(counts)
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count)
  }, [networkData])

  // Upper bound for the strength slider. Only meaningful when at least one edge
  // carries a numeric strength — weightless edges always pass the filter, so a
  // graph of purely structural edges would show an inert slider. Gate on real
  // strengths so the slider appears only when it can actually filter something.
  // Both Neptune weights (×10 → 10-70) and discovery scores (0-100) fit in 0-100.
  const weightMax = useMemo(() => {
    const strengths = (networkData?.edges ?? [])
      .map(edgeStrengthOf)
      .filter((s) => !Number.isNaN(s))
    if (!strengths.length) return 0
    return Math.max(100, Math.ceil(Math.max(...strengths) / 10) * 10)
  }, [networkData])

  // Reset the active relation set to "all" whenever the available relation set
  // changes (i.e. a new graph loads), so nothing is hidden by default. The
  // Min-strength slider restores the persisted per-graph value (0 if none), so
  // switching graphs picks up each graph's saved threshold instead of clearing.
  const edgeTypeSignature = availableEdgeTypes.map((e) => e.label).join('|')
  useEffect(() => {
    setActiveEdgeFilters(new Set(availableEdgeTypes.map((e) => e.label)))
    // Restore this graph's saved threshold, then mark the key settled so the
    // persist effect may write subsequent user changes for it.
    setMinEdgeWeight((persistKey ? getStoredMinStrength(persistKey) : undefined) ?? 0)
    settledKeyRef.current = persistKey
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edgeTypeSignature, persistKey])

  // Keep minEdgeWeight in range when the slider's upper bound shrinks on a graph
  // swap that shares edge labels but not strengths — otherwise a stale threshold
  // keeps being reported to the parent while the slider can't clear it. Only
  // clamp against a real bound (weightMax > 0); a weightMax of 0 means the graph
  // has no strength info yet (e.g. mid-load, networkData null), and clamping then
  // would zero — and, via the persist effect, erase — the stored per-graph value.
  useEffect(() => {
    if (weightMax > 0) setMinEdgeWeight((w) => (w > weightMax ? 0 : w))
  }, [weightMax])
  const toggleGroup = useCallback((groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(groupId)) next.delete(groupId)
      else next.add(groupId)
      return next
    })
  }, [])

  const handleNavigate = useCallback((route: string) => {
    // Open the detail page in a new tab so the user keeps their place in the
    // Octopus drawer and graph state isn't lost. Uses HashRouter-style URLs
    // (`/#/react/...`) so it matches the frontend's routing convention.
    const href = `${window.location.origin}/#${route}`
    window.open(href, '_blank', 'noopener,noreferrer')
  }, [])

  const { nodes, edges } = useMemo<{ nodes: Node[]; edges: Edge[] }>(() => {
    if (!networkData?.nodes?.length) return { nodes: [], edges: [] }

    // Apply the edge weight + relation-type filters before transform/layout.
    // Relation filter drops de-selected labels; weight filter drops edges below
    // the threshold (edges with no numeric weight always show).
    // Collapse exact duplicate edges first — the persisted graph can hold each
    // edge 2×+, which would otherwise produce colliding ReactFlow edge keys and
    // strand stale lines when a relation filter is toggled.
    const allEdges = dedupeEdges(networkData.edges)
    const filteredEdges = allEdges.filter((e) => {
      // Relation filter: show an edge only if its relation is checked. Empty set
      // = nothing checked = hide all (so "None" actually hides everything, and
      // unchecking a relation removes it instead of being a no-op).
      if (!activeEdgeFilters.has(edgeFilterKey(e))) return false
      // Strength filter: weightless edges (NaN) always pass.
      const s = edgeStrengthOf(e)
      if (minEdgeWeight > 0 && !Number.isNaN(s) && s < minEdgeWeight) return false
      return true
    })

    // When a filter actually removes edges, prune nodes that are no longer
    // reachable from the root — otherwise de-selecting a relation leaves its
    // child nodes dangling and filtering looks like it "does nothing". Nodes
    // that were already isolated in the unfiltered graph are kept.
    let prunedNodes = networkData.nodes
    if (filteredEdges.length !== allEdges.length) {
      const adj = new Map<string, string[]>()
      const link = (a: string, b: string) => {
        const arr = adj.get(a)
        if (arr) arr.push(b)
        else adj.set(a, [b])
      }
      for (const e of filteredEdges) {
        link(e.from_id, e.to_id)
        link(e.to_id, e.from_id)
      }
      const root = rootId || networkData.root || networkData.nodes[0]?.id
      const reachable = new Set<string>(root ? [root] : [])
      const queue: string[] = root ? [root] : []
      while (queue.length) {
        const cur = queue.shift()!
        for (const nb of adj.get(cur) ?? []) {
          if (!reachable.has(nb)) {
            reachable.add(nb)
            queue.push(nb)
          }
        }
      }
      const connectedInFull = new Set<string>()
      for (const e of allEdges) {
        connectedInFull.add(e.from_id)
        connectedInFull.add(e.to_id)
      }
      prunedNodes = networkData.nodes.filter((n) => reachable.has(n.id) || !connectedInFull.has(n.id))
    }
    const transformed = transformKrakenResponse({ ...networkData, nodes: prunedNodes, edges: filteredEdges })
    // Bundle >5 same-relation, same-domain leaf neighbours into GROUP nodes.
    const { nodes: rfNodes, edges: rfEdges } = collapseRelations(
      transformed.nodes,
      transformed.edges,
      expandedGroups,
    )
    // Nodes that are already the target of a `discovery` edge have been
    // investigated (directly or via deeper layers) — hide the button.
    const alreadyInvestigated = new Set<string>()
    for (const e of rfEdges) {
      const ed = (e.data as { edgeData?: { label?: string } } | undefined)?.edgeData
      if (ed?.label === 'discovery') alreadyInvestigated.add(e.target)
    }

    // Decorate each node with its expanding flag + pre-bound onExpand /
    // onNavigate callbacks so the popover's buttons can fire without
    // ReactFlow having to plumb the node id through onNodeClick.
    const decoratedNodes = rfNodes.map((n) => {
      const d = n.data as CircleNodeData
      if (d.kind === 'GROUP') {
        return { ...n, data: { ...(n.data as Record<string, unknown>), onToggleGroup: () => toggleGroup(n.id) } }
      }
      const route = targetRouteForNode(n.data as Partial<CircleNodeData> | undefined)
      // A discovered LINK is "already an entity" if the backend stamped it
      // (domain match on retrieve) OR we created one for it this session.
      const entityAlreadyExists =
        !!(d.original as { entity_exists?: boolean } | undefined)?.entity_exists ||
        (createdEntityNodeIds?.has(n.id) ?? false)
      return {
        ...n,
        data: {
          ...(n.data as Record<string, unknown>),
          expanding: expandingNodeIds?.has(n.id) ?? false,
          onExpand: onExpandNode && isOceanInternalNode(d) ? () => onExpandNode(n.id) : undefined,
          onNavigate: route ? () => handleNavigate(route) : undefined,
          discovering: discoveringNodeIds?.has(n.id) ?? false,
          // Show "Investigate online" on external/temporary nodes too (fingerprint
          // sites, scraped links, Apify social posts) — the host seeds their url/name
          // from the graph node, so the backend can investigate without a Neptune
          // vertex. Only hide it on synthetic `discovery_*` results from a prior
          // investigation (those carry no seed url and re-investigating is circular).
          onDiscoverWeb: onDiscoverWebNode && !alreadyInvestigated.has(n.id) && !n.id.startsWith('discovery_')
            ? (opts: { layers: number; categories?: string[] }) =>
                onDiscoverWebNode(n.id, opts)
            : undefined,
          // Baked on every node; the button only renders when the node carries
          // an image (searched-image anchor, product thumbnail, social screenshot).
          onReverseImageSearch: onReverseImageSearchNode,
          // Baked on every node; the OSINT button only renders when the node's
          // value is a recognisable email / phone identifier.
          onInvestigateEmailOsint: onInvestigateEmailOsintNode,
          onInvestigatePhoneOsint: onInvestigatePhoneOsintNode,
          onCreateEntity: CREATE_ENTITY_KINDS.has(d.kind) && d.original?.id
            ? () => {
                const field = KIND_TO_PRELINK_FIELD[d.kind]
                if (!field) return
                const rawId = String(d.original!.id)
                setCreateEntityPreLink({
                  field,
                  id: stripVertexPrefix(rawId),
                  label: d.fullTitle || d.label || rawId,
                })
                setCreateEntitySourceId(rawId)
                createEntityModal.onOpen()
              }
            : undefined,
          // Any DISCOVERED web source not yet backed by an Ocean record (a bare
          // LINK, a social profile, or a discovered site) → let the AI agent
          // scrape it, reason the entity type, and create a CustomEntity. We're
          // turning THIS node into an entity, not expanding it. Gated on "not an
          // Ocean-internal record" + a web-source kind + the entity not already
          // existing — backend stamps `entity_exists` on retrieve (domain match),
          // and `createdEntityNodeIds` hides it optimistically right after an
          // in-session create, before the graph reloads.
          entityExists: entityAlreadyExists,
          onAiCreateEntity:
            onCreateEntityNode &&
            !isOceanInternalNode(d) &&
            (d.kind === 'LINK' || d.kind === 'SOCIAL' || d.kind === 'SAW' || d.kind === 'PRODUCT') &&
            !entityAlreadyExists
              ? () => onCreateEntityNode(n.id)
              : undefined,
          creatingEntity: creatingEntityNodeIds?.has(n.id) ?? false,
        },
      }
    })
    const laidOut = radialTreeLayout(decoratedNodes, rfEdges, rootId, {
      centerX: 0,
      centerY: 0,
    })

    // Tight-cluster pass: an EXPANDED group's members should huddle around the
    // group node as a compact cluster of objects, not be flung across the
    // global layout. Pack them into concentric rings hugging the group node.
    const posById = new Map(laidOut.map((n) => [n.id, { ...n.position }]))
    const clusteredIds = new Set<string>()
    for (const gnode of rfNodes) {
      const gd = gnode.data as CircleNodeData
      if (gd.kind !== 'GROUP' || gd.collapsed) continue
      const gp = posById.get(gnode.id)
      if (!gp) continue
      const memberIds = rfEdges
        .filter((e) => (e.source === gnode.id || e.target === gnode.id) && e.id !== `e-${gnode.id}`)
        .map((e) => (e.source === gnode.id ? e.target : e.source))
      const SP = 64 // tight packing step
      let idx = 0
      let ring = 1
      while (idx < memberIds.length) {
        const r = ring * SP * 0.92
        const cap = Math.max(1, Math.floor((2 * Math.PI * r) / SP))
        for (let k = 0; k < cap && idx < memberIds.length; k++) {
          const a = -Math.PI / 2 + (2 * Math.PI * k) / cap + ring * 0.5
          posById.set(memberIds[idx], { x: gp.x + r * Math.cos(a), y: gp.y + r * Math.sin(a) })
          clusteredIds.add(memberIds[idx])
          idx++
        }
        ring++
      }
    }
    const positioned = laidOut.map((n) => ({
      ...n,
      position: posById.get(n.id) ?? n.position,
      data: clusteredIds.has(n.id) ? { ...(n.data as Record<string, unknown>), clustered: true } : n.data,
    }))

    // Route each edge through the handle closest to the other node so the
    // bezier curve doesn't wrap around the source node.
    const routedEdges = rfEdges.map((e) => {
      const s = posById.get(e.source)
      const t = posById.get(e.target)
      if (!s || !t) return e
      const angle = Math.atan2(t.y - s.y, t.x - s.x)
      const sourceDir = cardinalForAngle(angle)
      const targetDir = cardinalForAngle(angle + Math.PI)
      return {
        ...e,
        sourceHandle: `s-${sourceDir}`,
        targetHandle: `t-${targetDir}`,
      }
    })

    return { nodes: positioned, edges: routedEdges }
  }, [networkData, rootId, expandingNodeIds, onExpandNode, handleNavigate, discoveringNodeIds, onDiscoverWebNode, onReverseImageSearchNode, onInvestigateEmailOsintNode, onInvestigatePhoneOsintNode, onCreateEntityNode, creatingEntityNodeIds, createdEntityNodeIds, expandedGroups, toggleGroup, activeEdgeFilters, minEdgeWeight])

  // Re-frame the graph whenever the set of nodes changes. ReactFlow's `fitView`
  // prop only fits on the initial render, so without this the view drifts as
  // nodes stream in during discovery (or after the user pans/zooms) and the
  // graph appears to "disappear" — especially now that the zoom/fit Controls
  // are gone. Keyed on the node-id signature so it fits on add/remove, not on
  // every pan. A rAF lets ReactFlow register the new nodes first.
  const { fitView } = useReactFlow()
  const nodeSignature = nodes.map((n) => n.id).join('|')
  useEffect(() => {
    if (!nodes.length) return
    const raf = requestAnimationFrame(() => {
      fitView({ padding: 0.25, duration: 350, maxZoom: 1 })
    })
    return () => cancelAnimationFrame(raf)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeSignature, fitView])

  if (isLoading) {
    return (
      <Box w="100%" h="100%" display="flex" alignItems="center" justifyContent="center">
        <Text fontSize="12px" color="neutral.400">Building network...</Text>
      </Box>
    )
  }

  if (!networkData?.nodes?.length) {
    return (
      <Box w="100%" h="100%" display="flex" alignItems="center" justifyContent="center">
        <Text fontSize="12px" color="neutral.400">No network data</Text>
      </Box>
    )
  }

  return (
    <Box
      w="100%"
      h="100%"
      position="relative"
      sx={{
        // Force pointer-events on ReactFlow node wrappers — ReactFlow disables them
        // when nodesDraggable=false. Override at multiple levels for specificity.
        '.react-flow__node': { pointerEvents: 'all !important' },
        '.react-flow__node > *': { pointerEvents: 'all !important' },
        '.react-flow__node-toolbar': { pointerEvents: 'all !important' },
        '.react-flow__edgelabel-renderer > *': { pointerEvents: 'all' },
      }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        panOnScroll
        zoomOnScroll
        zoomOnPinch
        minZoom={0.05}
        maxZoom={1}
        proOptions={{ hideAttribution: true }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={true}
        // Disable ReactFlow's global keyboard shortcuts (Backspace/Delete to
        // remove selected nodes, Cmd+A to select all, etc.). Without this,
        // ReactFlow attaches document-level keydown listeners that steal
        // keystrokes from the Ocean AI chat input rendered next to it.
        deleteKeyCode={null}
        selectionKeyCode={null}
        multiSelectionKeyCode={null}
        zoomActivationKeyCode={null}
        panActivationKeyCode={null}
        // Click on the node body itself is now a no-op — expansion is
        // triggered explicitly from the Expand button inside the hover
        // popover (see CircleNode → NodeTooltipContent → ExpandButton).
        onNodeClick={() => {}}
        defaultEdgeOptions={{
          type: 'kraken',
        }}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={16}
          size={1}
          color="rgba(124, 58, 237, 0.15)"
        />
      </ReactFlow>

      {showEdgeFilter && availableEdgeTypes.length > 0 && (
        <Box position="absolute" top="10px" left="10px" w="256px" zIndex={10}>
          <EdgeFilterPanel
            availableEdgeTypes={availableEdgeTypes}
            activeFilters={activeEdgeFilters}
            onFilterChange={setActiveEdgeFilters}
            minWeight={minEdgeWeight}
            onMinWeightChange={weightMax > 0 ? setMinEdgeWeight : undefined}
            weightMax={weightMax}
            weightStep={5}
          />
        </Box>
      )}

      {onSaveGraph && (
        <SaveGraphButton
          onSave={onSaveGraph}
          isSaving={isSavingGraph}
          // Sit right of the edge-filter slider when it's shown; otherwise align
          // to the panel's left edge so the button doesn't float in the middle.
          left={showEdgeFilter && availableEdgeTypes.length > 0 ? '276px' : '10px'}
        />
      )}

      <AddEntityModal
        isOpen={createEntityModal.isOpen}
        onClose={() => {
          createEntityModal.onClose()
          setCreateEntityPreLink(null)
          setCreateEntitySourceId(null)
        }}
        preLink={createEntityPreLink ?? undefined}
        onCreated={(entity) => handleEntityCreated(entity, createEntitySourceId)}
      />
    </Box>
  )
}

const KrakenNetworkTree = (props: KrakenNetworkTreeProps) => (
  <ReactFlowProvider>
    <KrakenNetworkTreeInner {...props} />
  </ReactFlowProvider>
)

export default KrakenNetworkTree
